import { createFileRoute, Link, notFound, redirect, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod";
import { SiteShell } from "@/components/site/SiteShell";
import { BlogCategoryArchive } from "@/components/blog/BlogCategoryArchive";
import { SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd, ROBOTS_NOINDEX_NOFOLLOW } from "@/lib/seo";
import { publicBlogCategoryQuery, publicBlogCategoryCountsQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache, markNotFoundResponse } from "@/lib/cache-headers";
import { getBlogCategory, BLOG_CATEGORY_SITEMAP_MIN_POSTS } from "@/lib/blog-categories";
import { resolveLegacyBlogCategoryPath } from "@/lib/blog-legacy-redirects";
import type { BlogPostCard } from "@/components/blog/blog-types";

const searchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1).default(1),
});

export const Route = createFileRoute("/blog/category/$category")({
  validateSearch: searchSchema,
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  beforeLoad: ({ params }) => {
    // Retired WordPress taxonomies 301 straight to their canonical page so we
    // never redirect Googlebot into the category 404.
    if (!getBlogCategory(params.category)) {
      const legacy = resolveLegacyBlogCategoryPath(params.category);
      if (legacy) throw redirect({ href: legacy, statusCode: 301, throw: true });
    }
  },
  loader: async ({ params, context }) => {
    await setEdgeHtmlCache();
    const category = getBlogCategory(params.category);
    if (!category) throw notFound();
    const [list, counts] = await Promise.all([
      context.queryClient.ensureQueryData(publicBlogCategoryQuery({ category: category.id })),
      context.queryClient.ensureQueryData(publicBlogCategoryCountsQuery()),
    ]);
    return {
      categoryId: category.id,
      posts: (list?.posts ?? []) as BlogPostCard[],
      counts: counts?.counts ?? {},
    };
  },
  head: ({ loaderData, params }) => {
    const category = getBlogCategory(params.category);
    if (!category) {
      return {
        meta: [
          { title: "Category not found · Shugert" },
          { name: "robots", content: ROBOTS_NOINDEX_NOFOLLOW },
        ],
        links: [],
      };
    }
    const count = (loaderData?.posts ?? []).length;
    const thin = count < BLOG_CATEGORY_SITEMAP_MIN_POSTS;
    return buildHead({
      locale: "en",
      title: `${category.title} · Shugert Insights`,
      description: category.description,
      path: `/blog/category/${category.id}`,
      altPath: `/blog/category/${category.id}`,
      noindex: thin,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/blog" },
          { name: category.label, path: `/blog/category/${category.id}` },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/blog/category/${category.id}#collection`,
          name: category.title,
          description: category.description,
          url: `${SITE_URL}/blog/category/${category.id}`,
          isPartOf: { "@type": "Blog", "@id": `${SITE_URL}/blog#blog` },
        },
      ],
    });
  },
  notFoundComponent: () => {
    markNotFoundResponse();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">
            404
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-foreground">
            Category not found
          </h1>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
          >
            Browse insights
          </Link>
        </div>
      </SiteShell>
    );
  },
  component: BlogCategoryRoute,
});

function BlogCategoryRoute() {
  const { categoryId, posts, counts } = Route.useLoaderData();
  const { page } = Route.useSearch();
  const category = getBlogCategory(categoryId)!;
  return (
    <SiteShell>
      <BlogCategoryArchive category={category} posts={posts} counts={counts} page={page} />
    </SiteShell>
  );
}
